import FastAPIInstance from '../../../services/risksystem3-public-api';
import {
  RaptorPlatformInputModel,
  RaptorPlatformModel,
  RaptorPlatformUpdateInputModel,
} from './types';

// API Functions for React Query

/**
 * Get all RaptorPlatforms for a client
 * @param client_id - The client ID
 * @param include_inactive - Whether to include inactive platforms
 * @returns Promise<RaptorPlatformModel[]>
 */
export const getRaptorPlatformsForClient = async (
  client_id: string,
  include_inactive: boolean = false,
): Promise<RaptorPlatformModel[]> => {
  return (
    await FastAPIInstance.get<RaptorPlatformModel[]>(
      `/v1/raptor_platform/${client_id}?include_inactive=${include_inactive}`,
    )
  ).data;
};

/**
 * Get a specific RaptorPlatform by ID
 * @param client_id - The client ID
 * @param raptor_platform_id - The platform ID
 * @param include_inactive - Whether to include inactive platforms
 * @returns Promise<RaptorPlatformModel>
 */
export const getRaptorPlatformById = async (
  client_id: string,
  raptor_platform_id: string,
  include_inactive: boolean = false,
): Promise<RaptorPlatformModel> => {
  return (
    await FastAPIInstance.get<RaptorPlatformModel>(
      `/v1/raptor_platform/${client_id}/${raptor_platform_id}?include_inactive=${include_inactive}`,
    )
  ).data;
};

/**
 * Create a new RaptorPlatform
 * @param input - The platform input model
 * @returns Promise<RaptorPlatformModel>
 */
export const createRaptorPlatform = async (
  input: RaptorPlatformInputModel,
): Promise<RaptorPlatformModel> => {
  return (
    await FastAPIInstance.post<RaptorPlatformModel>(
      '/v1/raptor_platform/',
      input,
    )
  ).data;
};

/**
 * Update an existing RaptorPlatform
 * @param input - The platform update input model
 * @returns Promise<RaptorPlatformModel>
 */
export const updateRaptorPlatform = async (
  input: RaptorPlatformUpdateInputModel,
): Promise<RaptorPlatformModel> => {
  return (
    await FastAPIInstance.put<RaptorPlatformModel>(
      '/v1/raptor_platform/',
      input,
    )
  ).data;
};

/**
 * Deactivate a RaptorPlatform
 * @param client_id - The client ID
 * @param raptor_platform_id - The platform ID
 * @returns Promise<void>
 */
export const deactivateRaptorPlatform = async (
  client_id: string,
  raptor_platform_id: string,
): Promise<void> => {
  await FastAPIInstance.delete(
    `/v1/raptor_platform/${client_id}/${raptor_platform_id}`,
  );
};

// React Query Key Factory
export const raptorPlatformKeys = {
  all: ['raptor-platform'] as const,
  lists: () => [...raptorPlatformKeys.all, 'list'] as const,
  list: (client_id: string, include_inactive?: boolean) =>
    [...raptorPlatformKeys.lists(), client_id, include_inactive] as const,
  details: () => [...raptorPlatformKeys.all, 'detail'] as const,
  detail: (
    client_id: string,
    raptor_platform_id: string,
    include_inactive?: boolean,
  ) =>
    [
      ...raptorPlatformKeys.details(),
      client_id,
      raptor_platform_id,
      include_inactive,
    ] as const,
};
